/**
 * @fileoverview conflictsApi - API layer for entitlement conflicts functionality
 *
 * Provides RTK Query endpoints for searching and retrieving entitlement conflicts.
 * Handles API communication for risk conflicts, violating entitlements, and
 * related conflict data with proper response transformation.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../../../../../shared/services/baseQuery';

import {
  EntitlementConflictsType,
  type SearchEntitlementConflictsApiResponse,
  type SearchEntitlementConflictsRequest,
} from './conflictsTypes';

export const entitlementConflictsApi = createApi({
  reducerPath: EntitlementConflictsType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  tagTypes: ['ConflictDetails'],
  keepUnusedDataFor: 300,
  endpoints: () => ({}),
});

export const entitlementConflictsApiEndpoints =
  entitlementConflictsApi.injectEndpoints({
    endpoints: (builder) => ({
      searchEntitlementConflicts: builder.mutation<
        unknown,
        SearchEntitlementConflictsRequest
      >({
        query: ({ riskKey, functionId, assignedEntitlementId, payload }) => ({
          url: `/api/sodms/inherent-risk/${riskKey}/functions/${functionId}/assigned-entitlements/${assignedEntitlementId}/search-violating-entitlements`,
          method: 'POST',
          data: payload,
          isECMv6: true,
        }),
        transformResponse: (
          response: SearchEntitlementConflictsApiResponse
        ) => {
          if (response.status === 200) {
            // Return the full data without transformation to preserve all fields
            return {
              data: response.data,
              searchAfter: response.searchAfter,
              afterKey: response.afterKey,
              message: response.message,
              count: response.count,
            };
          }

          return {
            data: [],
            searchAfter: null,
            afterKey: null,
            message: '',
            count: 0,
          };
        },
      }),
    }),
  });

export const { useSearchEntitlementConflictsMutation } =
  entitlementConflictsApiEndpoints;
